import React from 'react';
import fisaCoolingOffClosureReasons from './fisaCoolingOffClosureReasons';
import Select from '../../../Form/Select/Select';
import TextAreaInput from '../../../Form/TextInput/TextAreaInput';
import './FisaCoolingOffClosure.scss';
import Button from '../../../Form/Button/Button';
import {useUnifiedAnalytics} from '../../../../contexts/AnalyticsWrapper';

/** @import { OptionType } from '../../../Form/Select/SelectTypes' */
/** @import { FisaCoolingOffClosureStepOneProps } from './FisaAccountClosureTypes' */

/**
 * FISA Cooling Off Closure Step One Component
 * @param {FisaCoolingOffClosureStepOneProps} props
 */
function FisaCoolingOffClosureStepOne({ reason, setReason, freeTextReason, setFreeTextReason, payoutDestination, setPayoutDestination, showPayoutDestinations, eligibleDisaAccountOptions, payoutDestinationAccount, setPayoutDestinationAccount, onContinue, onCancel }) {
    const [showSelectValidation, setShowSelectValidation] = React.useState(false);
    const analytics = useUnifiedAnalytics();
    const freeTextLimit = 250;
    
    /**
     * Sets a value and clears validation errors
     * @param {OptionType} val
     * @param {(val: OptionType) => void} set
     */
    const setAndClearValidation = (val, set) => {
        setShowSelectValidation(false);
        set(val);
    };

    /**
     * Handle dropdown selection change with analytics tracking
     * @param {OptionType} value - Selected option
     */
    const handleClosureReasonChange = (value) => {
        if (value) {
            analytics.trackAnalyticsClickEvent(
                `fisa cooling off closure reason ${value.label}`,
                window.location.href,
                'fisa-cooling-off-closure',
                'fisa_1yr',
                '',
                {},
                'dropdown selection',
                `close_fisa_account|dropdown_click|${value.value}`,
                'account_closure_cooling_off'
            );
            setAndClearValidation(value, setReason);
        }
    };

    /**
     * Sets free text with character limit
     * @param {string} val
     */
    const setFreeTextWithLimit = (val) => {
        if (val.length <= freeTextLimit) setFreeTextReason(val);
    };

    // Button enable logic
    const isReasonSelected = !!reason;
    const needsText = reason && (reason.value === "other" || reason.value === "unhappy_with_service");
    const isTextValid = needsText ? freeTextReason.trim().length > 0 : true;
    const isPayoutDestinationAccountSelected = payoutDestination !== 'instant-access-cash-isa' || !!payoutDestinationAccount;
    const canContinue = isReasonSelected && isTextValid && isPayoutDestinationAccountSelected;

    return (
        <div className="fisaCoolingOff__content">
            <span className="fisaCoolingOff__sorryTitle">We&apos;re sorry to hear you&apos;d like to close your account.</span>
            <div className="fisaCoolingOff__dropdownSection">
                <p className="fisaCoolingOff__dropdownLabel">
                    What is the reason for closing your account?
                </p>
                <div className="fisaCoolingOff__inputWrap modal__slim modal__slim--extra-margin">
                    <Select
                        options={fisaCoolingOffClosureReasons}
                        placeholder="Select a reason"
                        onChange={option => option && handleClosureReasonChange(option)}
                        className="fisaCoolingOff__select"
                        value={reason}
                    />
                    <div className={showSelectValidation ? "login__error" : "login__error login__error--hide"}>
                        <span className="login__error--wrap">Please select a reason.</span>
                    </div>
                </div>
                {reason && reason.value === "unhappy_with_service" && (
                    <div className="fisaCoolingOff__inputWrap modal__slim modal__slim--extra-margin">
                        <TextAreaInput
                            onChange={val => setFreeTextWithLimit(val)}
                            placeholder="Please tell us more about what you were unhappy with"
                            value={freeTextReason}
                        />
                        <span className="account-closure__char-count">{freeTextReason.length}/{freeTextLimit} characters used</span>
                    </div>
                )}
                {reason && reason.value === "other" && (
                    <div className="fisaCoolingOff__inputWrap modal__slim modal__slim--extra-margin">
                        <TextAreaInput
                            onChange={val => setFreeTextWithLimit(val)}
                            placeholder="Please specify your reason for closing the account"
                            value={freeTextReason}
                        />
                        <span className="account-closure__char-count">{freeTextReason.length}/{freeTextLimit} characters used</span>
                    </div>
                )}
            </div>
            {showPayoutDestinations && (
                <fieldset className="fisaCoolingOff__payoutSection">
                    <legend>Where would you like your funds to be paid?</legend>
                    <div className="fisaCoolingOff__payoutOptions">
                        <label className={`fisaCoolingOff__payoutOption ${payoutDestination === 'linked-account' ? 'fisaCoolingOff__payoutOption--selected' : ''}`}>
                            <input
                                type="radio"
                                name="fisa-cooling-off-payout-destination"
                                value="linked-account"
                                checked={payoutDestination === 'linked-account'}
                                onChange={() => setPayoutDestination('linked-account')}
                            />
                            <span>
                                <strong>To my linked current account</strong>
                                <small>We&apos;ll close your account and pay your full balance to your linked account. You&apos;ll lose the tax-free status on the entire amount you withdraw.</small>
                            </span>
                        </label>
                        <label className={`fisaCoolingOff__payoutOption ${payoutDestination === 'instant-access-cash-isa' ? 'fisaCoolingOff__payoutOption--selected' : ''}`}>
                            <input
                                type="radio"
                                name="fisa-cooling-off-payout-destination"
                                value="instant-access-cash-isa"
                                checked={payoutDestination === 'instant-access-cash-isa'}
                                onChange={() => setPayoutDestination('instant-access-cash-isa')}
                            />
                            <span>
                                <strong>Transfer to an Investec Save Instant Access Cash ISA</strong>
                                <small>We&apos;ll transfer your balance to an Instant Access Cash ISA. Your savings will keep their tax-free status, and any interest earned to date will be paid into your linked account.</small>
                            </span>
                        </label>
                    </div>
                    {payoutDestination === 'instant-access-cash-isa' && (
                        <div className="fisaCoolingOff__destinationAccount">
                            <label htmlFor="fisa-cooling-off-destination-account">Choose the account you&apos;d like to transfer to</label>
                            <Select
                                inputId="fisa-cooling-off-destination-account"
                                options={eligibleDisaAccountOptions}
                                placeholder="Select account"
                                onChange={option => setPayoutDestinationAccount(option || null)}
                                value={payoutDestinationAccount}
                            />
                        </div>
                    )}
                </fieldset>
            )}
            <div className="fisaCoolingOff__buttonRow">
                <Button
                    variant="secondary"
                    size="med"
                    label="Cancel"
                    onClick={onCancel}
                />
                <Button
                    size="med"
                    label="Continue"
                    disabled={!canContinue}
                    onClick={onContinue}
                />
            </div>
        </div>
    );
}

export default FisaCoolingOffClosureStepOne;